import React, { useRef, useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
} from '@mui/material';
import {
  Print as PrintIcon,
  Email as EmailIcon,
  Close as CloseIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import QRCode from 'qrcode';
import { useProductosMaterialContext } from 'context';
import { useAmbienteEcf, ambienteEcfValido } from 'lib/ambienteEcf';
import { FP, fpInputSx } from 'components/financepro';
import DocumentoEmpresarialPreview from './DocumentoEmpresarialPreview';
import { generarHtmlDocumentoEmpresarial, money, fechaEmisionDgii, resolverFechaVencimiento } from './documentoEmpresarial';

// Ambiente e-CF de la DGII (TesteCF / CerteCF / ecf). Configurable por entorno.

const CONFIGURACION_DEFAULT = {
  plantillaPorDefecto: '80mm-basica',
  leyendaPiePagina: '',
  mensajeGracias: 'Gracias por su compra',
  mensajeGarantia: '',
  mensajeDevolucion: '',
  mostrarInfoEmpresa: true,
  nombreEmpresa: '',
  direccionEmpresa: '',
  telefonoEmpresa: '',
  emailEmpresa: '',
  rncEmpresa: '',
  sitioWebEmpresa: '',
  mostrarNCF: true,
  mostrarObservaciones: true,
  mostrarDescuentos: true,
  mostrarImpuestos: true,
  mostrarSubtotalAntesDescuentos: true,
  cortarPapelAutomatico: false,
  imprimirLogo: false,
  rutaLogo: '',
  mostrarCodigoBarras: false,
  mostrarQR: false,
  textoQR: '',
};

/**
 * VistaNotaCredito: vista previa de la nota de crédito con formato empresarial.
 * La configuración se obtiene de configuracion_factura (tipo_documento Nota de Credito).
 */
const rncSoloDigitos = (...valores) => {
  for (const valor of valores) {
    if (valor == null || valor === false || valor === 'false') continue;
    const digits = String(valor).replace(/\D/g, '');
    if (digits.length >= 9) return digits;
  }
  return '';
};

const VistaNotaCredito = ({ open, onClose, notaCreditoData = {} }) => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const {
    usuarioLog,
    claveLog,
    nombreNegocio,
    direccion_empresa: direccionEmpresaCtx,
    rnc_empresa: rncEmpresaCtx,
  } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const nombreEmpresaContexto =
    typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
      ? nombreNegocio.trim()
      : '';
  const direccionEmpresaContexto =
    typeof direccionEmpresaCtx === 'string' && direccionEmpresaCtx.trim()
      ? direccionEmpresaCtx.trim()
      : '';
  const rncEmpresaContexto = rncSoloDigitos(rncEmpresaCtx);

  const printRef = useRef(null);
  const [dialogoEmailAbierto, setDialogoEmailAbierto] = useState(false);
  const [emailDestino, setEmailDestino] = useState('');
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [configuracion, setConfiguracion] = useState(CONFIGURACION_DEFAULT);
  const [cargandoConfig, setCargandoConfig] = useState(false);

  const {
    items = [],
    impuestos = [],
    subtotal = 0,
    totalDescuentos = 0,
    totalImpuestos = 0,
    total = 0,
    documento = '',
    numero = '',
    fecha = new Date().toISOString().split('T')[0],
    ncf = '',
    tipoDocumento = '',
    observaciones = '',
    empresa = {},
    cliente_nombre = '',
    ncf_factura_origen = '',
    nfact_origen = '',
    tipofactura_origen = '',
  } = notaCreditoData;

  // Comprobante fiscal electrónico: el QR de la DGII y la fecha de validez del NCF
  // solo se muestran cuando el emisor lo indica ('S').
  const esElectronico = String(notaCreditoData.comprobanteelectronico || '').toUpperCase() === 'S';

  
  const codigoSeguridad = String(notaCreditoData.codigo_seguridad || '').trim();
  const fechaFirma = String(notaCreditoData.fecha_firma || '').trim();

  
  const fechaVencimiento = useMemo(() => {
    const codigo = String(notaCreditoData.codigocomprobante || '').trim();
    const ncfQr = String(ncf || '').toUpperCase();
    if (codigo === '34' || ncfQr.startsWith('E34')) return '';
    return resolverFechaVencimiento(notaCreditoData);
  }, [notaCreditoData, ncf]);

  const rncEmisor = rncSoloDigitos(
    notaCreditoData.rncemisor,
    notaCreditoData.rnc_empresa,
    empresa?.rnc,
    empresa?.rnc_empresa,
    empresa?.rncEmpresa,
    configuracion.rncEmpresa,
    rncEmpresaCtx,
    notaCreditoData.id_empresa?.rnc,
    notaCreditoData.id_empresa?.rnc_empresa,
    notaCreditoData.id_empresa?.rncEmpresa,
    selectedCompany?.rnc,
    selectedCompany?.rnc_empresa,
    selectedCompany?.rncEmpresa,
  );

  // URL de consulta de timbre DGII. La nota E34 puede ir con o sin RNC del
  // comprador (p. ej. consumo E32 sin identificación). RncComprador solo se
  // incluye cuando hay un RNC/cédula válido.
  // Ambiente de la emisión si viene; si no, el del tenant (varinicial.ambienteecf).
  const ambienteTenant = useAmbienteEcf();
  const ambienteQr = ambienteEcfValido(notaCreditoData.ambiente) || ambienteTenant;

  const qrUrl = useMemo(() => {
    if (!esElectronico || !ambienteQr) return '';
    const ncfQr = String(ncf || '').toUpperCase();
    if (!ncfQr) return notaCreditoData.qrUrl || '';
    const rncComprador = String(notaCreditoData.rnc || '').replace(/\D/g, '');
    const rncCompradorValido =
      rncComprador &&
      rncComprador !== '999999' &&
      (rncComprador.length === 9 || rncComprador.length === 11);
    const montoFuente = notaCreditoData.monto_total_ecf ?? (total || 0);
    const montoTotal = Number(montoFuente).toFixed(2);
    const fechaEmisionDgiiFmt = fechaEmisionDgii(fecha);
    const partes = [
      `RncEmisor=${encodeURIComponent(rncEmisor)}`,
      ...(rncCompradorValido ? [`RncComprador=${encodeURIComponent(rncComprador)}`] : []),
      `ENCF=${encodeURIComponent(ncfQr)}`,
      `FechaEmision=${encodeURIComponent(fechaEmisionDgiiFmt)}`,
      `MontoTotal=${encodeURIComponent(montoTotal)}`,
      `FechaFirma=${encodeURIComponent(fechaFirma)}`,
      `CodigoSeguridad=${encodeURIComponent(codigoSeguridad)}`,
    ];
    return `https://ecf.dgii.gov.do/${ambienteQr}/ConsultaTimbre?${partes.join('&')}`;
  }, [
    esElectronico,
    notaCreditoData.qrUrl,
    notaCreditoData.rnc,
    notaCreditoData.monto_total_ecf,
    ncf,
    total,
    fecha,
    rncEmisor,
    codigoSeguridad,
    fechaFirma,
    ambienteQr,
  ]);

  // Genera el QR como data URL (funciona en la vista previa y en la impresión).
  const [qrDataUrl, setQrDataUrl] = useState('');
  useEffect(() => {
    if (!open || !esElectronico || !qrUrl) {
      setQrDataUrl('');
      return undefined;
    }
    let cancelado = false;
    QRCode.toDataURL(qrUrl, { margin: 1, width: 220 })
      .then((url) => {
        if (!cancelado) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelado) setQrDataUrl('');
      });
    return () => {
      cancelado = true;
    };
  }, [open, esElectronico, qrUrl]);

  const tipoDocConfig =
    (notaCreditoData.tipo_documento || notaCreditoData.tipoDocumentoConfig || 'Nota de Credito').trim() ||
    'Nota de Credito';

  const totalNotaCredito = 0;
  const totalAPagar = Number(total);

  useEffect(() => {
    if (!open) return;
    const idEmpresa = notaCreditoData.id_empresa;
    const dbEmpresa = idEmpresa && typeof idEmpresa === 'object' ? idEmpresa.db_empresa : null;
    if (!dbEmpresa) {
      setConfiguracion(CONFIGURACION_DEFAULT);
      return;
    }
    setCargandoConfig(true);
    fetch(servidor + '/api/facturabackend_obtenerconfiguracion', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id_empresa: idEmpresa,
        usuario: usuarioLog || '',
        password: claveLog || '',
        tipo_documento: tipoDocConfig,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.configuracion) {
          setConfiguracion({ ...CONFIGURACION_DEFAULT, ...data.configuracion });
        }
      })
      .catch(() => setConfiguracion(CONFIGURACION_DEFAULT))
      .finally(() => setCargandoConfig(false));
  }, [open, notaCreditoData.id_empresa, tipoDocConfig, usuarioLog, claveLog, servidor]);

  /**
   * Configuración resuelta: prioriza nombre / dirección / RNC del contexto
   * (login). Si el contexto no los trae, se conservan los de la configuración
   * guardada para que la impresión no quede sin datos de empresa.
   */
  const configuracionResuelta = {
    ...configuracion,
    nombreEmpresa: nombreEmpresaContexto || configuracion.nombreEmpresa || '',
    direccionEmpresa: direccionEmpresaContexto || configuracion.direccionEmpresa || '',
    rncEmpresa: rncEmisor || rncEmpresaContexto || configuracion.rncEmpresa || '',
  };

  const subtotalAntesDescuentos = items.reduce(
    (s, i) => s + parseFloat(i.cantidad || 0) * parseFloat(i.precio || 0),
    0
  );

  const tituloDoc = tipoDocumento || 'Nota de Crédito';

  const filasTotales = [{ label: 'Subtotal Gravado', value: subtotalAntesDescuentos }];
  if (totalDescuentos > 0) {
    filasTotales.push({ label: 'Descuentos', value: `−${money(totalDescuentos)}`, danger: true });
  }
  filasTotales.push({ label: 'Subtotal neto', value: subtotal });
  
  impuestos.forEach((imp) => {
    filasTotales.push({ label: imp.nombre || 'Impuesto', value: Number(imp.monto || 0) });
  });
  filasTotales.push({ label: 'Total Itbis', value: totalImpuestos });
  

  const metaExtra = [];
  if (configuracionResuelta.mostrarNCF !== false && ncf) {
    metaExtra.push({ label: ncf.startsWith('E') ? 'e-NCF' : 'NCF', value: ncf });
  }
  if (ncf_factura_origen) {
    metaExtra.push({ label: ncf_factura_origen.startsWith('E') ? 'e-NCF Modificado' : 'NCF Modificado', value: ncf_factura_origen });
  }

  const bloqueExtra =
    (nfact_origen != null && nfact_origen !== '') || tipofactura_origen
      ? {
          titulo: 'Factura origen',
          lineas: [
            `${tipofactura_origen || '—'} — Nº ${nfact_origen}`,
            ncf_factura_origen ? `NCF: ${ncf_factura_origen}` : '',
          ].filter(Boolean),
        }
      : null;

  const parte = cliente_nombre
    ? { titulo: 'Cliente', nombre: cliente_nombre, lineas: [`RNC: ${notaCreditoData.rnc}`] }
    : null;

  const handleImprimir = () => {
    if (cargandoConfig) {
      showError('Cargando configuración de impresión, intente nuevamente en un momento');
      return;
    }
    const ventana = window.open('', '_blank');
    if (!ventana) {
      showError('Permita ventanas emergentes para imprimir');
      return;
    }
    const htmlContent = generarHtmlDocumentoEmpresarial({
      tituloDocumento: tituloDoc,
      numero,
      documento,
      fecha,
      fechaVencimiento,
      metaExtra,
      parte,
      bloqueExtra,
      items,
      filasTotales,
      totalLabel: notaCreditoData.codigocomprobante === '33' ? "TOTAL " : "TOTAL ",
      totalValue: totalAPagar,
      observaciones: configuracionResuelta.mostrarObservaciones !== false ? observaciones : '',
      qrDataUrl: esElectronico ? qrDataUrl : '',
      codigoSeguridad: esElectronico ? codigoSeguridad : '',
      fechaFirma: esElectronico ? fechaFirma : '',
      mensajeGracias: configuracionResuelta.mensajeGracias || '',
      leyendaPiePagina: configuracionResuelta.leyendaPiePagina || '',
      nombreEmpresa: configuracionResuelta.nombreEmpresa || 'Empresa',
      direccionEmpresa: configuracionResuelta.direccionEmpresa || '',
      telefonoEmpresa: configuracionResuelta.telefonoEmpresa || empresa?.telefono || '',
      rncEmpresa: configuracionResuelta.rncEmpresa || '',
      emailEmpresa: configuracionResuelta.emailEmpresa || '',
      mostrarInfoEmpresa: configuracionResuelta.mostrarInfoEmpresa !== false,
 
    });
    ventana.document.write(htmlContent);
    ventana.document.close();
    ventana.focus();
    setTimeout(() => {
      ventana.print();
      ventana.close();
    }, 250);
  };

  const abrirDialogoEmail = () => setDialogoEmailAbierto(true);
  const cerrarDialogoEmail = () => {
    setDialogoEmailAbierto(false);
    setEmailDestino('');
  };

  const handleEnviarEmail = async () => {
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    const email = (emailDestino || '').trim();
    if (!email) {
      showError('Ingrese el correo de destino');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showError('Correo electrónico no válido');
      return;
    }
    setEnviandoEmail(true);
    try {
      const response = await fetch(servidor + '/api/facturabackend_enviarfactura_email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: notaCreditoData.id_empresa,
          usuario: usuarioLog || '',
          password: claveLog || '',
          email_destino: email,
          factura: {
            items,
            impuestos,
            subtotal,
            totalDescuentos,
            totalImpuestos,
            total,
            totalNotaCredito,
            totalAPagar,
            documento,
            numero,
            fecha,
            ncf,
            tipoDocumento: tipoDocumento || 'Nota de Credito',
            observaciones,
            empresa: configuracionResuelta.nombreEmpresa || empresa?.empresa || empresa?.nombre || '',
            direccionEmpresa: configuracionResuelta.direccionEmpresa || '',
            rncEmpresa: configuracionResuelta.rncEmpresa || '',
          },
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && !data.Error) {
        showSuccess('Nota de crédito enviada por correo correctamente');
        cerrarDialogoEmail();
      } else {
        showError(data.Error || 'No se pudo enviar el correo. Compruebe la configuración del servidor.');
      }
    } catch {
      showError('Error de conexión al enviar el correo');
    } finally {
      setEnviandoEmail(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            maxHeight: '92vh',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.2,
              bgcolor: FP.color.navySoft,
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ReceiptLongIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography
              sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '0.98rem', lineHeight: 1.2 }}
            >
              {tituloDoc} · Nº {numero || '—'}
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.72rem' }}>
              Vista previa · Formato empresarial
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Button
            startIcon={<CloseIcon />}
            onClick={onClose}
            size="small"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cerrar
          </Button>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, md: 3 }, bgcolor: FP.color.surface }}>
          <DocumentoEmpresarialPreview
            printRef={printRef}
            mostrarInfoEmpresa={configuracionResuelta.mostrarInfoEmpresa !== false}
            nombreEmpresa={configuracionResuelta.nombreEmpresa || 'Empresa'}
            direccionEmpresa={configuracionResuelta.direccionEmpresa || ''}
            telefonoEmpresa={configuracionResuelta.telefonoEmpresa || empresa?.telefono || ''}
            rncEmpresa={configuracionResuelta.rncEmpresa || ''}
            emailEmpresa={configuracionResuelta.emailEmpresa || ''}
            tituloDocumento={tituloDoc}
            numero={numero}
            documento={documento}
            fecha={fecha}
            fechaVencimiento={notaCreditoData.codigocomprobante === '33' ? fechaVencimiento : ''}
            metaExtra={metaExtra}
            parte={parte}
            bloqueExtra={bloqueExtra}
            items={items}
            filasTotales={filasTotales}
            totalLabel={notaCreditoData.codigocomprobante === '33' ? "TOTAL " : "TOTAL "}
            totalValue={totalAPagar}
            observaciones={
              configuracionResuelta.mostrarObservaciones !== false ? observaciones : ''
            }
            qrDataUrl={esElectronico ? qrDataUrl : ''}
            codigoSeguridad={esElectronico ? codigoSeguridad : ''}
            fechaFirma={esElectronico ? fechaFirma : ''}
            mensajeGracias={configuracionResuelta.mensajeGracias || ''}
            footerNote={configuracionResuelta.leyendaPiePagina || ''}
            varianteTabla="listado"
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            startIcon={<PrintIcon />}
            onClick={handleImprimir}
            disabled={cargandoConfig}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.4,
              borderColor: FP.color.border,
              color: FP.color.textPrimary,
              '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
            }}
          >
            {cargandoConfig ? 'Cargando formato…' : 'Imprimir'}
          </Button>
          <Button
            startIcon={<EmailIcon />}
            onClick={abrirDialogoEmail}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.4,
              borderColor: FP.color.border,
              color: FP.color.textPrimary,
              '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
            }}
          >
            Enviar por email
          </Button>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: 1.4,
              fontWeight: 700,
              bgcolor: FP.color.navy,
              color: '#FFFFFF',
              boxShadow: 'none',
              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogoEmailAbierto}
        onClose={cerrarDialogoEmail}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            fontWeight: 700,
            color: FP.color.textPrimary,
            fontSize: '0.98rem',
          }}
        >
          Enviar nota de crédito por correo
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
          <TextField
            autoFocus
            fullWidth
            label="Correo electrónico"
            type="email"
            value={emailDestino}
            onChange={(e) => setEmailDestino(e.target.value)}
            placeholder="ejemplo@correo.com"
            disabled={enviandoEmail || !usuarioLog}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 1, ...fpInputSx }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            onClick={cerrarDialogoEmail}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleEnviarEmail}
            variant="contained"
            disabled={enviandoEmail || !usuarioLog}
            sx={{
              textTransform: 'none',
              borderRadius: 1.4,
              fontWeight: 700,
              bgcolor: FP.color.navy,
              color: '#FFFFFF',
              boxShadow: 'none',
              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
            }}
          >
            {enviandoEmail ? 'Enviando…' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VistaNotaCredito;
